import React, { createContext, useContext, useState, useEffect } from "react";

export const CURRENCIES = [
  { code: "INR", symbol: "₹",  name: "Indian Rupee",      flag: "🇮🇳", country: "India" },
  { code: "USD", symbol: "$",  name: "US Dollar",          flag: "🇺🇸", country: "USA" },
  { code: "GBP", symbol: "£",  name: "British Pound",      flag: "🇬🇧", country: "UK" },
  { code: "AED", symbol: "د.إ",name: "UAE Dirham",         flag: "🇦🇪", country: "UAE" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar",   flag: "🇸🇬", country: "Singapore" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar",  flag: "🇦🇺", country: "Australia" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar",    flag: "🇨🇦", country: "Canada" },
];

// Map ISO country codes → currency code
const COUNTRY_TO_CURRENCY = {
  IN: "INR",
  US: "USD", PR: "USD", GU: "USD",
  GB: "GBP",
  AE: "AED",
  SG: "SGD",
  AU: "AU", // handled below
  CA: "CAD",
};
// Full list of countries that use each currency
const COUNTRY_CURRENCY_MAP = {
  // INR
  IN: "INR",
  // USD
  US: "USD", PR: "USD", GU: "USD", VI: "USD", AS: "USD", MH: "USD", FM: "USD", PW: "USD",
  // GBP
  GB: "GBP",
  // AED
  AE: "AED",
  // SGD
  SG: "SGD",
  // AUD
  AU: "AUD", NZ: "AUD",
  // CAD
  CA: "CAD",
};

const detectCurrencyByCountry = (countryCode) => {
  const code = COUNTRY_CURRENCY_MAP[countryCode];
  return CURRENCIES.find(c => c.code === code) || null;
};

export const CurrencyContext = createContext({
  currency: CURRENCIES[0],
  setCurrency: () => {},
  formatPrice: () => "",
  getPrice: () => 0,
});

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    const saved = localStorage.getItem("mh_currency");
    return CURRENCIES.find(c => c.code === saved) || CURRENCIES[0];
  });

  // Auto-detect country on first visit (when no saved preference)
  useEffect(() => {
    const saved = localStorage.getItem("mh_currency");
    if (saved) return; // user already has a saved preference, respect it

    fetch("https://ipapi.co/json/")
      .then(r => r.json())
      .then(data => {
        const detected = detectCurrencyByCountry(data.country_code);
        if (detected) {
          setCurrencyState(detected);
          localStorage.setItem("mh_currency", detected.code);
        }
      })
      .catch(() => {
        // silently fall back to INR default on error
      });
  }, []);

  const setCurrency = (cur) => {
    setCurrencyState(cur);
    localStorage.setItem("mh_currency", cur.code);
  };

  /* Get price for a course given its currencyPrices object */
  const getPrice = (currencyPrices, fallbackPrice) => {
    if (currencyPrices && currencyPrices[currency.code] > 0) {
      return currencyPrices[currency.code];
    }
    // fallback: use INR price or the generic price field
    if (currencyPrices?.INR > 0) return currencyPrices.INR;
    return fallbackPrice ? parseFloat(fallbackPrice) || 0 : 0;
  };

  /* Format price with currency symbol */
  const formatPrice = (currencyPrices, fallbackPrice) => {
    const amount = getPrice(currencyPrices, fallbackPrice);
    if (!amount) return "Free";
    return `${currency.symbol}${amount.toLocaleString()}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, getPrice, CURRENCIES }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
